// src/components/TemporaryErrorScreen.js
//
// Écran d'indisponibilité TEMPORAIRE (réseau coupé, 502 pendant un
// redémarrage de l'API, timeout…). À distinguer soigneusement de
// `ErrorBoundary`, qui traite les erreurs de RENDU React : ici, l'application
// va parfaitement bien, c'est le serveur qui n'a pas répondu.
//
// Raison d'être (incident 2026-08-19) : sans cet état, `ProtectedRoute`
// traitait toute erreur non-401 comme « profil non vérifié » et éjectait des
// membres déjà onboardés vers /verify-profile. Un écran explicite + un bouton
// « Réessayer » valent mieux qu'une redirection déduite d'une absence
// d'information.
//
// Le message rassure explicitement sur l'intégrité des données : l'utilisateur
// qui voit une erreur au milieu d'un parcours suppose d'abord qu'il a perdu
// quelque chose.

import React from 'react'
import { useTranslation } from 'react-i18next'
import { CButton, CCard, CCardBody } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilReload, cilCloudy } from '@coreui/icons'

/**
 * @param {object} props
 * @param {() => void} props.onRetry - relance la vérification en cours.
 * @param {boolean} [props.retrying] - une tentative est en cours.
 * @param {boolean} [props.fullHeight] - occupe la hauteur de la fenêtre
 *   (cas d'un écran affiché avant que le layout applicatif ne soit monté).
 */
const TemporaryErrorScreen = ({ onRetry, retrying = false, fullHeight = true }) => {
  const { t } = useTranslation()

  return (
    <div
      className="d-flex justify-content-center align-items-center p-3"
      style={fullHeight ? { minHeight: '100vh' } : undefined}
    >
      <CCard className="border-0 shadow-sm" style={{ maxWidth: '440px', borderRadius: '16px' }}>
        <CCardBody className="text-center p-4 p-md-5">
          <div
            className="d-inline-flex align-items-center justify-content-center mb-3"
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              // Teinte portée par la variable de thème CoreUI : l'encart reste
              // lisible en clair comme en sombre (cf. §7 scss/style.scss, on
              // n'écrit jamais une couleur de fond en dur).
              background: 'rgba(var(--cui-warning-rgb), 0.15)',
            }}
          >
            <CIcon icon={cilCloudy} size="xl" className="text-warning" />
          </div>

          <h5 className="fw-bold mb-2">{t('common:temporary_error.title')}</h5>

          <p className="text-body-secondary mb-1" style={{ fontSize: '14px' }}>
            {t('common:temporary_error.message')}
          </p>
          <p className="text-body-secondary mb-4" style={{ fontSize: '14px' }}>
            {t('common:temporary_error.hint')}
          </p>

          <CButton
            color="primary"
            onClick={onRetry}
            disabled={retrying}
            style={{ borderRadius: '10px', fontWeight: 600 }}
          >
            <CIcon icon={cilReload} className="me-2" />
            {retrying ? t('common:loading') : t('common:retry')}
          </CButton>
        </CCardBody>
      </CCard>
    </div>
  )
}

export default TemporaryErrorScreen
